import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FeatureFlags, FeatureMetadata, SubscriptionPlan } from '@/types/subscription';

interface FeatureRegistryProps {
  features: FeatureMetadata[];
  plans: SubscriptionPlan[];
  planFlags: Record<string, FeatureFlags>;
  loading: boolean;
  syncLoading: boolean;
  onSync: () => void;
  onNewFeature: () => void;
  onToggleFeature: (planId: string, key: string) => void;
  onEditFeature: (feature: FeatureMetadata) => void;
  onDeleteFeature: (key: string) => void;
  onNewPlan: () => void;
  onEditPlan: (plan: SubscriptionPlan) => void;
}

export function FeatureRegistry({
  features,
  plans,
  planFlags,
  loading,
  syncLoading,
  onSync,
  onNewFeature,
  onToggleFeature,
  onEditFeature,
  onDeleteFeature,
  onNewPlan,
  onEditPlan
}: FeatureRegistryProps) {
  return (
    <Box sx={{ mt: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Feature Registry</Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={syncLoading ? <CircularProgress size={16} /> : <SettingsIcon />}
            onClick={onSync}
            disabled={syncLoading}
          >
            Transfer Feature Flags
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onNewPlan}
          >
            Create New Plan
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNewFeature}
          >
            Create New Feature
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                {plans.map(plan => (
                  <TableCell key={plan.id} align="center" sx={{ fontWeight: 700 }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                      <Box>
                        {plan.name}
                        <Typography variant="caption" display="block" color="text.secondary">
                          ID: {plan.id}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => onEditPlan(plan)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features.map(feature => (
                <TableRow key={feature.key} hover>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {feature.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {feature.key}
                    </Typography>
                    {feature.showOnPricing && (
                      <Chip label="Pricing" size="small" variant="outlined" color="info" sx={{ ml: 1, height: 16, fontSize: '0.6rem' }} />
                    )}
                  </TableCell>
                  {plans.map(plan => (
                    <TableCell key={`${feature.key}-${plan.id}`} align="center">
                      <IconButton
                        onClick={() => onToggleFeature(plan.id, feature.key)}
                        size="small"
                      >
                        {planFlags[plan.id]?.[feature.key] ? (
                          <ToggleOnIcon sx={{ color: 'success.main' }} />
                        ) : (
                          <ToggleOffIcon sx={{ color: 'error.main' }} />
                        )}
                      </IconButton>
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="Edit Metadata">
                        <IconButton size="small" onClick={() => onEditFeature(feature)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Feature">
                        <IconButton size="small" color="error" onClick={() => onDeleteFeature(feature.key)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {features.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={plans.length + 2} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No features registered.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={plans.length + 2} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
